import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Calendar, MapPin, Clock, Users, DollarSign, CheckCircle2,
  Loader2, ArrowRight, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffingJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForm, setApplyForm] = useState({ cover_letter: '', why_hire: '', experience: '', availability: '', phone: '' });
  const autoApplyAttempted = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/staffing/jobs/${jobId}`);
        setJob(res.data);
      } catch (e) {
        toast.error('Job not found');
      } finally { setLoading(false); }
    })();
  }, [jobId]);

  // Auto-open the apply dialog after return from login/register with ?apply=1
  useEffect(() => {
    if (autoApplyAttempted.current) return;
    if (!job || !token || !user) return;
    if (searchParams.get('apply') !== '1') return;
    const alreadyApplied = (job.applicants || []).some((a) => a.user_id === user.user_id);
    if (alreadyApplied) {
      setSearchParams({}, { replace: true });
      return;
    }
    autoApplyAttempted.current = true;
    setApplyOpen(true);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, token, user]);

  const apply = async () => {
    if (!token) {
      const returnTo = encodeURIComponent(`/staffing/jobs/${jobId}?apply=1`);
      navigate(`/login?returnTo=${returnTo}`);
      return;
    }
    setApplyOpen(true);
  };

  const submitApplication = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/staffing/jobs/${jobId}/apply`, applyForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Application submitted — the poster will review and reach out.');
      setApplyOpen(false);
      const fresh = await axios.get(`${API}/staffing/jobs/${jobId}`);
      setJob(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply');
    } finally { setApplying(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!job) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Job not found</div>;

  const hasApplied = !!(token && user && (job.applicants || []).some((a) => a.user_id === user.user_id));
  const isFilled = job.status !== 'open' || (job.slots_filled || 0) >= (job.slots_needed || 1);

  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 space-y-6" data-testid="staffing-job-detail">
        <Link to="/staffing" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to all jobs
        </Link>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 uppercase text-[10px]">
                    {job.position_type}{job.is_custom_position ? ' · custom' : ''}
                  </Badge>
                  <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 text-[10px]">{job.position_label}</Badge>
                  <Badge className={`text-[10px] ${job.status === 'open' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-muted'}`}>{job.status}</Badge>
                </div>
                <h1 className="text-3xl font-bold font-['Space_Grotesk']" data-testid="job-detail-title">{job.title}</h1>
                {job.event_name && <p className="text-sm text-muted-foreground mt-1">For event: {job.event_name}</p>}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold tabular-nums">${job.base_rate}/hr</div>
                <div className="text-xs text-muted-foreground">{job.hours} hrs · {job.slots_needed} slot{job.slots_needed > 1 ? 's' : ''}</div>
              </div>
            </div>

            {job.description && (
              <div className="prose prose-invert max-w-none text-sm whitespace-pre-wrap">{job.description}</div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-border">
              <Info icon={Calendar} label="Date" value={fmtDate(job.date)} />
              <Info icon={Clock} label="Time" value={`${job.start_time} – ${job.end_time}`} />
              <Info icon={MapPin} label="Location" value={job.location} />
              <Info icon={Users} label="Slots" value={`${job.slots_filled || 0} / ${job.slots_needed}`} />
              <Info icon={DollarSign} label="Estimated earnings" value={`$${((job.worker_earnings_per_worker ?? (job.base_rate * ((new Date(`2000-01-01T${job.end_time}`) - new Date(`2000-01-01T${job.start_time}`)) / 3600000))) || 0).toFixed(2)} per worker`} />
              <Info icon={ShieldCheck} label="Posted by" value={job.poster_name || 'Eventism partner'} />
            </div>

            {(job.requirements || []).length > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Requirements</div>
                <ul className="text-sm space-y-1">
                  {job.requirements.map((r, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />{r}</li>)}
                </ul>
              </div>
            )}

            <div className="pt-4 border-t border-border">
              {hasApplied ? (
                <Button className="w-full" disabled data-testid="detail-applied-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Applied
                </Button>
              ) : isFilled ? (
                <Button className="w-full" disabled data-testid="detail-filled-btn">Positions Filled</Button>
              ) : (
                <Button
                  className="w-full h-12 bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold"
                  onClick={apply}
                  disabled={applying}
                  data-testid="detail-apply-btn"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  {token ? `Apply Now — $${job.base_rate}/hr` : 'Sign in or sign up to apply'}
                </Button>
              )}
              {!token && (
                <p className="text-[11px] text-muted-foreground text-center mt-2">
                  New to Eventism? <Link to={`/register?returnTo=/staffing/jobs/${jobId}?apply=1`} className="text-indigo-400 hover:underline">Create your staff account</Link>
                  {' — takes 30 seconds.'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Apply for: {job.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-lime-500/10 border border-lime-500/30 p-3 text-xs">
                <strong className="text-lime-400">{job.position_label}</strong> · ${job.base_rate}/hr
                <div className="text-muted-foreground mt-0.5">You'll earn ${((job.worker_earnings_per_worker ?? (job.base_rate * (job.hours || 0))) || 0).toFixed(2)} if selected.</div>
              </div>
              <div>
                <Label htmlFor="apply-cover">Cover letter / introduction *</Label>
                <Textarea id="apply-cover" rows={4} value={applyForm.cover_letter} onChange={(e) => setApplyForm({ ...applyForm, cover_letter: e.target.value })} placeholder="Tell the poster a bit about yourself and why this gig interests you." data-testid="apply-cover-letter" />
              </div>
              <div>
                <Label htmlFor="apply-why">Why should they hire you?</Label>
                <Textarea id="apply-why" rows={3} value={applyForm.why_hire} onChange={(e) => setApplyForm({ ...applyForm, why_hire: e.target.value })} placeholder="What makes you the right fit?" data-testid="apply-why-hire" />
              </div>
              <div>
                <Label htmlFor="apply-exp">Relevant experience</Label>
                <Textarea id="apply-exp" rows={3} value={applyForm.experience} onChange={(e) => setApplyForm({ ...applyForm, experience: e.target.value })} placeholder="Past gigs, certifications, references…" data-testid="apply-experience" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="apply-avail">Availability confirmation</Label>
                  <Input id="apply-avail" value={applyForm.availability} onChange={(e) => setApplyForm({ ...applyForm, availability: e.target.value })} placeholder="e.g. Confirmed available the full shift" data-testid="apply-availability" />
                </div>
                <div>
                  <Label htmlFor="apply-phone">Phone</Label>
                  <Input id="apply-phone" value={applyForm.phone} onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })} placeholder="So the poster can reach you" data-testid="apply-phone" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
              <Button
                className="bg-lime-500 hover:bg-lime-600 text-slate-900"
                onClick={submitApplication}
                disabled={applying || !applyForm.cover_letter.trim()}
                data-testid="submit-application-btn"
              >
                {applying ? 'Submitting…' : 'Submit application'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-indigo-400 mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value || '—'}</div>
      </div>
    </div>
  );
}
